/**
 * EntityPickerBase — Reusable combobox picker for FK fields with optional inline create.
 *
 * Handles the Popover + Command shell, search, clear, and "Add New" action.
 * Entity-specific pickers (OrganizationPicker, ContactPicker) wrap this with their
 * own data fetching, item rendering, and Quick Create Dialog.
 *
 * See docs/design-system/ui-detail-and-form-pages.md § FK Picker with Inline Create.
 */
import { useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  ChevronsUpDown,
  ListFilter,
  Pencil,
  Plus,
  Search,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Label } from '../../../components/ui/label';
import { FacetOptionRow } from './FacetOptionRow';
import { resolveCandidateRows, type EntityFilterCandidates } from './filterFacets';

/**
 * Dropdown sizing: shrink-to-fit the widest row, never narrower than the anchor
 * (trigger width, or 280px for the compact pencil trigger), never wider than the
 * viewport allows. A row carries a check + entity icon (~48px) that the trigger
 * doesn't, so pinning the panel to the trigger width truncated labels that fit
 * perfectly well in the trigger itself. The 28rem ceiling keeps a long entity
 * name from stretching the panel across a wide screen — that one truncates.
 */
export const POPOVER_WIDTH =
  'p-0 w-auto max-w-[min(28rem,var(--radix-popover-content-available-width))]';

/**
 * Dropdown height: cap the PANEL to the space Radix measures between the trigger
 * and the viewport edge, and let the results list — not the pinned search input —
 * absorb the shortfall.
 *
 * Radix flips to whichever side has more room but never SHRINKS the panel, so a
 * fixed inner height (`CommandList`'s `max-h-[300px]`, plus the input) simply runs
 * off the bottom of the screen when neither side fits it — and the overflowing
 * rows cannot be scrolled into view, because the scroll container itself is what
 * is off-screen. Same idiom as `ListFilters` and `SelectContent`.
 */
const POPOVER_HEIGHT =
  'flex flex-col max-h-[var(--radix-popover-content-available-height)]';

export interface EntityPickerBaseProps<T extends { id: string }>
  extends EntityFilterCandidates {
  /** Currently selected entity ID */
  value: string;
  /**
   * List of entities to display.
   *
   * IGNORED for the option rows while `candidates` is set (facet mode — see
   * {@link EntityFilterCandidates}); it is still read for the SELECTED value's
   * label, which is how a value outside the facet keeps its name.
   */
  items: T[];

  // --- Display ---
  /** Get display label for an entity (shown in trigger when selected) */
  getItemLabel: (item: T) => string;
  /** Render the content of each dropdown item */
  renderItem: (item: T, isSelected: boolean) => ReactNode;
  /** Optional label above the picker */
  label?: string;
  /** Placeholder shown in the trigger button when nothing is selected */
  placeholder: string;
  /** Placeholder inside the search input */
  searchPlaceholder: string;
  /** Message when search yields no results */
  emptyMessage: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional CSS classes on the wrapper */
  className?: string;
  /** Show required indicator on label */
  required?: boolean;
  /** id applied to the trigger button (so an external <Label htmlFor> can target it) */
  triggerId?: string;
  /**
   * Accessible name for the trigger when the picker renders NO visible `label`
   * and no external `<Label htmlFor>` targets it.
   *
   * `role="combobox"` is not a name-from-content role, so the placeholder inside
   * the button does NOT become the control's accessible name the way a plain
   * button's text would: a label-less picker announces as an unnamed combobox.
   * Where several pickers sit in one form — one per document line — that also
   * makes them mutually indistinguishable to anything addressing them by name.
   */
  ariaLabel?: string;
  /** Render the trigger with a destructive border (validation error) */
  hasError?: boolean;
  /**
   * Human-readable name of the scope this picker is filtered to (e.g. an
   * organization or customer name). When set, a muted "Filtered to {name}"
   * header is shown at the top of the dropdown so the user knows the list — and
   * any entity they create — is scoped to that context, not the whole tenant.
   */
  contextLabel?: string | null;

  // --- Search ---
  /** Current search text (controlled) */
  search: string;
  /** Search text change handler */
  onSearchChange: (search: string) => void;

  // --- Popover state ---
  /** Whether the popover is open (controlled) */
  open: boolean;
  /** Popover open/close handler */
  onOpenChange: (open: boolean) => void;

  // --- Selection ---
  /** Called when an item is selected */
  onSelect: (id: string) => void;
  /** Show "None" option to clear selection */
  allowClear?: boolean;
  /** Called when the clear option is selected */
  onClear?: () => void;
  /** Custom label for the clear option (defaults to "None"). Useful for "All …" semantics. */
  clearLabel?: string;

  // --- Create action ---
  /**
   * Opens the owning module's Quick Create Dialog, seeded from the search text.
   *
   * This is the SINGLE source of truth for the inline-create row: pass a handler
   * and the row renders, omit it (or pass `undefined`) and the picker is
   * select-only. There is no separate "hide" flag, so a visible "+ Create" row
   * can never be backed by a handler that does nothing. Callers that show the
   * row conditionally pass `onAddNew={canCreate ? handleAddNew : undefined}`.
   *
   * Mirrors `EntityMatchPickerPopover`'s `onCreateNew`.
   */
  onAddNew?: () => void;
  /** Label for the "Add New" action. Required whenever `onAddNew` is set. */
  addNewLabel?: string;

  // --- Entity link ---
  /** URL to the selected entity's detail page. When set, the selected label becomes a clickable link with a pencil edit icon. */
  selectedEntityUrl?: string | null;
  /** Icon for the entity type, shown next to the link in linked trigger mode */
  entityIcon?: LucideIcon;

  // --- Popover styling ---
  /** Additional CSS classes for the PopoverContent (e.g., min-width override) */
  popoverClassName?: string;
}

export function EntityPickerBase<T extends { id: string }>({
  value,
  items,
  getItemLabel,
  renderItem,
  label,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled,
  className,
  required,
  triggerId,
  ariaLabel,
  hasError,
  contextLabel,
  search,
  onSearchChange,
  open,
  onOpenChange,
  onSelect,
  allowClear,
  onClear,
  clearLabel,
  addNewLabel,
  onAddNew,
  selectedEntityUrl,
  entityIcon: EntityIcon,
  popoverClassName,
  candidates,
  onShowAllCandidates,
  showAllCandidatesLabel,
}: EntityPickerBaseProps<T>) {
  const { t } = useTranslation('common');
  const [hovered, setHovered] = useState(false);

  // `combobox` is not a name-from-content role, so the trigger's visible text
  // gives it NO accessible name — a picker rendering its own label was reaching
  // screen readers (and `getByRole(…, { name })`) as an anonymous control. Wire
  // the label to the trigger; a caller supplying its own `triggerId` for an
  // external <Label htmlFor> keeps that id.
  const generatedTriggerId = useId();
  const resolvedTriggerId = triggerId ?? generatedTriggerId;

  const selectedItem = items.find((item) => item.id === value);

  // Cache the selected item's label so the linked trigger stays stable
  // even when search filtering removes the selected item from `items`
  const cachedLabelRef = useRef<{ id: string; label: string } | null>(null);
  if (selectedItem) {
    cachedLabelRef.current = { id: value, label: getItemLabel(selectedItem) };
  } else if (cachedLabelRef.current?.id !== value) {
    cachedLabelRef.current = null;
  }
  // The facet carries its own labels, so a selected value INSIDE it needs no
  // fetched item at all — which is what lets a page render a facet-sourced filter
  // before (or without) the picker's own list query resolving.
  const facetLabel = candidates?.find((c) => c.id === value)?.label;
  const resolvedLabel =
    facetLabel ?? (selectedItem ? getItemLabel(selectedItem) : cachedLabelRef.current?.label);

  // A value NOTHING resolves — filtered out of `items` by the caller, or deleted
  // from under the form — used to fall through to the id ITSELF, so the trigger
  // printed a raw UUID where every sibling printed a name (E7 capstone F10:
  // Inventory Settings showed `Goods Received Not Invoiced (GRNI) Account:
  // e3000000-…-000000000a02`). Say it cannot be shown instead: the PLACEHOLDER is
  // the wrong fallback for that case and always was, because it reads as "nothing
  // is set" while something IS — a quieter lie than the UUID.
  //
  // An EMPTY source is NOT that case. It is also exactly what this picker looks
  // like while its own list query is in flight, and every caller fetches that
  // list separately from the record holding the value — so warning on it would
  // flash "unavailable" over a value that is about to resolve, on every edit
  // form in the app. Warn only against a POPULATED source the value is
  // demonstrably absent from. "No options yet" and "this id is not among the
  // options" are different answers that must not look alike — the invariant
  // `filterFacets.ts` states for `ListFilterFacet.isLoading`, here applied to the
  // `items` path it could not reach. While the source is empty the trigger shows
  // the placeholder, exactly as it did before this warning existed.
  const hasPopulatedSource = candidates ? candidates.length > 0 : items.length > 0;
  const isUnresolved = Boolean(value) && resolvedLabel === undefined && hasPopulatedSource;
  const selectedLabel = resolvedLabel ?? (isUnresolved ? t('app.unresolvedSelection') : undefined);

  // Facet mode: offer exactly these, with the selected value pinned in even when
  // the facet no longer contains it. Both honesty invariants live in
  // `resolveCandidateRows` — see filterFacets.ts.
  const facetRows = useMemo(
    () =>
      candidates
        ? resolveCandidateRows({ candidates, search, value, selectedLabel })
        : null,
    [candidates, search, value, selectedLabel],
  );

  // When selectedEntityUrl is provided and an item is selected, render inline link + edit trigger.
  // An UNRESOLVED value gets no link: it would offer navigation to an entity this
  // control cannot even name, under the warning text standing in for its label.
  const showLinkedTrigger = selectedEntityUrl && value && resolvedLabel && !disabled;

  // Contextual filter header — shown at the top of the dropdown when the picker
  // is scoped to a named entity, so the user sees the active filter and knows a
  // newly created entity inherits that scope.
  const contextHeader = contextLabel ? (
    <div className="flex items-center gap-1.5 border-b px-3 py-2 text-xs text-muted-foreground">
      <ListFilter className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">
        {t('app.filteredToContext', { name: contextLabel })}
      </span>
    </div>
  ) : null;

  // One panel, rendered inside whichever trigger branch is active. Keeping a
  // single copy is deliberate: the two branches previously held identical
  // markup, so a fix to one silently missed the other.
  //
  // The empty state is an explicit `items.length === 0` check rather than
  // cmdk's <CommandEmpty>. Under `shouldFilter={false}` the consumer owns
  // filtering, and cmdk counts every rendered CommandItem — the always-present
  // "+ Create" row kept the count above zero, so <CommandEmpty> never fired
  // and `emptyMessage` was dead in every picker.
  const commandPanel = (
    // `min-h-0` on both flex boxes is what lets POPOVER_HEIGHT's cap actually
    // bite: a flex item's default `min-height: auto` refuses to shrink below its
    // content, so without it the panel keeps its natural height and overflows the
    // viewport exactly as before. The list is the only part that may shrink —
    // the search input stays pinned.
    <Command shouldFilter={false} className="min-h-0">
      {contextHeader}
      <CommandInput
        placeholder={searchPlaceholder}
        value={search}
        onValueChange={onSearchChange}
      />
      <CommandList className="min-h-0 flex-1">
        {(facetRows ?? items).length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </div>
        )}

        {allowClear && (
          <CommandGroup>
            <CommandItem value="__clear__" onSelect={onClear}>
              <span className="text-muted-foreground">{clearLabel ?? t('app.none')}</span>
            </CommandItem>
          </CommandGroup>
        )}

        {facetRows !== null
          ? facetRows.length > 0 && (
              <>
                {allowClear && <CommandSeparator />}
                <CommandGroup>
                  {facetRows.map((row) => (
                    <CommandItem
                      key={row.id}
                      value={row.id}
                      onSelect={() => onSelect(row.id)}
                    >
                      <FacetOptionRow label={row.label} count={row.count} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )
          : items.length > 0 && (
              <>
                {allowClear && <CommandSeparator />}
                <CommandGroup>
                  {items.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      onSelect={() => onSelect(item.id)}
                    >
                      {renderItem(item, item.id === value)}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

        {/* The escape hatch. It sits at the FOOT of the facet, below the
            candidates and above any create row, because it answers a question the
            facet cannot: "is this entity really absent from my list, or just from
            this shortlist?" — see filterFacets.ts, invariant 2. */}
        {facetRows !== null && onShowAllCandidates && (
          <>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                value="__show_all_candidates__"
                onSelect={onShowAllCandidates}
                className="text-muted-foreground touch:h-11"
              >
                <Search className="me-2 h-4 w-4 shrink-0" />
                {showAllCandidatesLabel ?? t('app.showAllOptions')}
              </CommandItem>
            </CommandGroup>
          </>
        )}

        {onAddNew && (
          <>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem onSelect={onAddNew} className="text-primary">
                <Plus className="me-2 h-4 w-4" />
                {addNewLabel}
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
    </Command>
  );

  return (
    <div className={cn('w-full', className)}>
      {label && (
        // The block + margin live on a WRAPPER, not on the Label. `Label`
        // renders its required marker as a SIBLING (so the asterisk stays out of
        // the accessible name), and a `block` Label pushes that sibling onto a
        // line of its own — a stranded red asterisk under the field name.
        <div className="mb-1.5">
          <Label htmlFor={resolvedTriggerId} required={required}>
            {label}
          </Label>
        </div>
      )}
      {showLinkedTrigger ? (
        <div
          className="relative group"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div
            className={cn(
              'flex w-full items-center rounded-md px-3 text-sm h-10 py-2',
              'transition-colors duration-150',
              'cursor-pointer',
              hovered && !open && 'bg-muted/50',
              open && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
            )}
          >
            {EntityIcon && (
              <EntityIcon className="h-4 w-4 me-2 flex-shrink-0 text-muted-foreground" />
            )}
            <Link
              to={selectedEntityUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 truncate text-primary hover:underline inline-flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="truncate">{selectedLabel}</span>
              <ArrowUpRight className="h-3 w-3 flex-shrink-0 opacity-60" />
            </Link>
            <Popover open={open} onOpenChange={onOpenChange}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  id={resolvedTriggerId}
                  className={cn(
                    'ms-2 p-1 rounded hover:bg-muted transition-colors flex-shrink-0',
                    hovered || open ? 'opacity-100' : 'opacity-0'
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenChange(true);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className={cn(POPOVER_WIDTH, POPOVER_HEIGHT, 'min-w-[280px]', popoverClassName)}
                align="start"
                collisionPadding={8}
              >
                {commandPanel}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      ) : (
        <Popover open={open} onOpenChange={onOpenChange}>
          <PopoverTrigger asChild>
            <Button
              id={resolvedTriggerId}
              variant="outline"
              role="combobox"
              aria-label={!label ? ariaLabel : undefined}
              aria-expanded={open}
              // The asterisk `required` draws is `aria-hidden` by design (it must
              // not join the label's accessible name), so without this a screen
              // reader gets no "required" signal at all on the control itself —
              // the visual half without the semantic one.
              aria-required={required || undefined}
              disabled={disabled}
              className={cn(
                'w-full justify-between font-normal',
                !value && 'text-muted-foreground',
                hasError && 'border-destructive',
              )}
            >
              {/* `min-w-0` on the flex wrapper, `truncate` on the text INSIDE it:
                  a flex item's default `min-width: auto` refuses to shrink below its
                  content, so putting both on one element loses the ellipsis. */}
              <span className={cn('flex min-w-0 items-center gap-1.5', isUnresolved && 'text-warning')}>
                {isUnresolved && (
                  <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                )}
                <span className="truncate">{selectedLabel ?? placeholder}</span>
              </span>
              <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={cn(
              POPOVER_WIDTH,
              POPOVER_HEIGHT,
              'min-w-[--radix-popover-trigger-width]',
              popoverClassName,
            )}
            align="start"
            // Radix subtracts this from the available-height POPOVER_HEIGHT caps
            // to, so the panel keeps a margin from the viewport edge instead of
            // sitting flush against it.
            collisionPadding={8}
          >
            {commandPanel}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
